import { useMemo } from "react";
import { AlertOctagon } from "lucide-react";
import { AuthError, AuthErrorCodes } from "firebase/auth";
import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Button } from "~/components/button";
import { Text } from "~/components/text";

export interface LoginLinkErrorProps {
  error?: AuthError;
}

function isUnregisteredUserError(error?: AuthError): boolean {
  return (
    error?.code === "auth/internal-error" &&
    error?.message?.includes("No organisation found")
  );
}

export function LoginLinkError({ error }: LoginLinkErrorProps) {
  const { t } = useTranslation(["login", "general"]);

  const { title, message } = useMemo(() => {
    if (error?.code === AuthErrorCodes.INVALID_OOB_CODE) {
      return {
        title: t("login:link.redirect.error.link-expired.title"),
        message: t("login:link.redirect.error.link-expired.message"),
      };
    }

    if (isUnregisteredUserError(error)) {
      return {
        title: t("login:link.redirect.error.unregistered-user.title"),
        message: t("login:link.redirect.error.unregistered-user.message"),
      };
    }

    return {
      title: t("login:link.redirect.error.unknown-error.title"),
      message: t("login:link.redirect.error.unknown-error.message"),
    };
  }, [t, error]);

  return (
    <div className="flex flex-col space-y-10 h-full items-center justify-center text-center max-w-xl mx-auto container">
      <div className="rounded-full bg-icon-error-ring p-3">
        <div className="rounded-full bg-icon-error-background p-2">
          <AlertOctagon className="text-system-error h-8 w-8" />
        </div>
      </div>

      <div className="space-y-2">
        <Text as="h1" variant="pageHeading">
          {title}
        </Text>
        <Text variant="subtle" className="whitespace-pre">
          {message}
        </Text>
      </div>

      <div className="grid grid-flow-col gap-3">
        <Link
          to="/contact-us"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="support-action"
        >
          <Button
            label={t("login:link.redirect.error.actions.support")}
            variant="tertiary"
            size="small"
            className="text-sm!"
          />
        </Link>

        <Link to="/login">
          <Button
            label={t("login:link.redirect.error.actions.login")}
            variant="primary"
            size="small"
            className="text-sm! font-normal"
          />
        </Link>
      </div>
    </div>
  );
}
